import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getExpenses, addExpense, deleteExpense, getSummary } from '../utils/api';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORIES = ['Food', 'Transport', 'Shopping', 'Bills', 'Health', 'Education', 'Entertainment', 'Other'];

const COLORS = ['#6c63ff', '#00d4aa', '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff922b', '#cc5de8'];

function Dashboard() {
    const today = new Date().toISOString().slice(0, 7);
    const [month, setMonth] = useState(today);
    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState({ total: 0, by_category: {}, count: 0 });
    const [form, setForm] = useState({ amount: '', category: 'Food', date: new Date().toISOString().slice(0, 10), note: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = async () => {
        try {
            const [expRes, sumRes] = await Promise.all([
                getExpenses(month),
                getSummary(month)
            ]);
            setExpenses(expRes.data);
            setSummary(sumRes.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData();
    }, [month]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.amount || !form.date) return;
        setLoading(true);
        setError('');
        try {
            await addExpense(form);
            setForm({ amount: '', category: 'Food', date: new Date().toISOString().slice(0, 10), note: '' });
            fetchData();
        } catch (err) {
            setError('Failed to add expense');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteExpense(id);
            fetchData();
        } catch (err) {
            console.error(err);
        }
    };

    const pieData = {
        labels: Object.keys(summary.by_category),
        datasets: [{
            data: Object.values(summary.by_category),
            backgroundColor: COLORS,
            borderWidth: 0,
        }]
    };

    const pieOptions = {
        plugins: {
            legend: {
                labels: { color: '#e2e8f0', font: { size: 12 } }
            }
        }
    };

    return (
        <div>
            <Navbar />
            <div className="main-content">
                <div className="page-title">
                    Dashboard <span>/ {month}</span>
                </div>

                {/* Month Picker */}
                <div className="month-picker">
                    <label style={{ color: 'var(--text2)', fontSize: '0.88rem' }}>Viewing month:</label>
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    />
                </div>

                {/* Stats Row */}
                <div className="stats-row">
                    <div className="stat-card">
                        <div className="stat-label">Total Spent</div>
                        <div className="stat-value">₹{summary.total.toLocaleString()}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Transactions</div>
                        <div className="stat-value">{summary.count}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Top Category</div>
                        <div className="stat-value" style={{ fontSize: '1.2rem' }}>
                            {Object.keys(summary.by_category).length > 0
                                ? Object.entries(summary.by_category).sort((a, b) => b[1] - a[1])[0][0]
                                : '—'}
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Avg per Day</div>
                        <div className="stat-value">
                            ₹{summary.count > 0 ? Math.round(summary.total / new Date(month + '-01').toLocaleString('default', { month: 'long' }).length) : 0}
                        </div>
                    </div>
                </div>

                {/* Add Expense Form */}
                <div className="card" style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '16px', fontSize: '0.95rem', color: 'var(--text2)' }}>ADD EXPENSE</h3>
                    {error && <div className="error-msg">{error}</div>}
                    <form className="expense-form" onSubmit={handleAdd}>
                        <input
                            type="number"
                            name="amount"
                            placeholder="Amount (₹)"
                            value={form.amount}
                            onChange={handleChange}
                            required
                            min="1"
                        />
                        <select name="category" value={form.category} onChange={handleChange}>
                            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                        <input
                            type="date"
                            name="date"
                            value={form.date}
                            onChange={handleChange}
                            required
                        />
                        <input
                            type="text"
                            name="note"
                            placeholder="Note (optional)"
                            value={form.note}
                            onChange={handleChange}
                        />
                        <button type="submit" className="btn-add" disabled={loading}>
                            {loading ? 'Adding...' : '+ Add'}
                        </button>
                    </form>
                </div>

                {/* Chart + List */}
                <div className="charts-row">
                    <div className="chart-card">
                        <h3>Spending by Category</h3>
                        {Object.keys(summary.by_category).length > 0
                            ? <Pie data={pieData} options={pieOptions} />
                            : <div className="empty-state"><p>No data yet</p></div>
                        }
                    </div>
                    <div className="chart-card">
                        <h3>Recent Expenses</h3>
                        {expenses.length > 0 ? (
                            <div className="expense-list">
                                {expenses.slice(0, 6).map(e => (
                                    <div key={e.id} className="expense-item">
                                        <div className="expense-left">
                                            <span className="expense-category">{e.category}</span>
                                            <div>
                                                <div className="expense-note">{e.note || '—'}</div>
                                                <div className="expense-date">{e.date}</div>
                                            </div>
                                        </div>
                                        <div className="expense-right">
                                            <span className="expense-amount">₹{e.amount}</span>
                                            <button className="btn-delete" onClick={() => handleDelete(e.id)}>🗑</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state"><p>No expenses this month</p></div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;